import { useCallback, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import ReactFlow, {
  Node,
  Controls,
  Background,
  MiniMap,
  addEdge,
  Connection,
  useNodesState,
  useEdgesState,
  NodeTypes,
  Panel,
  BackgroundVariant,
  Handle,
  Position,
} from 'reactflow';
import 'reactflow/dist/style.css';
import { workflowApi } from '../utils/api';
import { cn } from '../utils/cn';
import {
  Save,
  Play,
  ArrowLeft,
  Plus,
  Zap,
  Code,
  GitBranch,
  Globe,
  X,
  ChevronRight,
  Settings,
  Trash2,
  Copy,
  Clock,
  TrendingUp,
  Repeat,
  ArrowRightLeft,
  Wallet,
  Database,
  Brain,
  Bell,
  Activity,
  BarChart3,
  Shield,
  Link2,
  FileCode,
  Eye,
  Cpu,
  Radio,
  Layers,
  Calculator,
  Filter,
  TestTube,
  Pause,
  SkipForward,
} from 'lucide-react';
import { LucideIcon } from 'lucide-react';

// Node type definition
interface NodeDef {
  type: string;
  label: string;
  icon: LucideIcon;
  color: string;
  description: string;
  config: Record<string, any>;
}

// Supported blockchain networks
const SUPPORTED_CHAINS = [
  { id: 'ethereum', name: 'Ethereum', color: '#627EEA', symbol: 'ETH' },
  { id: 'bsc', name: 'BNB Smart Chain', color: '#F3BA2F', symbol: 'BNB' },
  { id: 'polygon', name: 'Polygon', color: '#8247E5', symbol: 'MATIC' },
  { id: 'arbitrum', name: 'Arbitrum', color: '#28A0F0', symbol: 'ETH' },
  { id: 'optimism', name: 'Optimism', color: '#FF0420', symbol: 'ETH' },
  { id: 'base', name: 'Base', color: '#0052FF', symbol: 'ETH' },
  { id: 'avalanche', name: 'Avalanche', color: '#E84142', symbol: 'AVAX' },
  { id: 'solana', name: 'Solana', color: '#9945FF', symbol: 'SOL' },
];

// DEX protocols per chain
const DEX_PROTOCOLS: Record<string, string[]> = {
  ethereum: ['uniswap-v2', 'uniswap-v3', 'sushiswap', 'curve', '1inch'],
  bsc: ['pancakeswap-v2', 'pancakeswap-v3', 'biswap', 'apeswap'],
  polygon: ['quickswap', 'uniswap-v3', 'sushiswap', 'balancer'],
  arbitrum: ['uniswap-v3', 'camelot', 'sushiswap', 'gmx'],
  optimism: ['velodrome', 'uniswap-v3', 'synthetix'],
  base: ['aerodrome', 'baseswap', 'uniswap-v3'],
  avalanche: ['traderjoe', 'pangolin', 'platypus'],
  solana: ['raydium', 'orca', 'jupiter'],
};

// Smart Contract / DeFi focused node categories
const nodeCategories: Array<{
  name: string;
  icon: LucideIcon;
  description: string;
  nodes: NodeDef[];
}> = [
  {
    name: 'Triggers',
    icon: Zap,
    description: 'Start your bot based on conditions',
    nodes: [
      { type: 'price-trigger', label: 'Price Alert', icon: TrendingUp, color: '#00d4aa', description: 'Trigger when price crosses threshold', config: { chain: 'ethereum', asset: '', condition: 'above', price: 0 } },
      { type: 'schedule', label: 'Schedule', icon: Clock, color: '#6366f1', description: 'Run on time interval (DCA, rebalance)', config: { interval: '1h', cron: '' } },
      { type: 'onchain-event', label: 'On-Chain Event', icon: Radio, color: '#8b5cf6', description: 'Listen to smart contract events', config: { chain: 'ethereum', contract: '', event: '', abi: [] } },
      { type: 'oracle-update', label: 'Oracle Update', icon: Activity, color: '#f0b90b', description: 'Trigger on Chainlink/Pyth update', config: { chain: 'ethereum', oracle: 'chainlink', feed: 'ETH/USD' } },
      { type: 'pool-change', label: 'Pool Change', icon: Layers, color: '#f6465d', description: 'Detect liquidity pool changes', config: { chain: 'ethereum', dex: 'uniswap-v3', pool: '', threshold: 5 } },
      { type: 'mempool-watch', label: 'Mempool Watch', icon: Eye, color: '#00d4aa', description: 'Monitor pending transactions', config: { chain: 'ethereum', filter: 'swap', minValue: 0 } },
      { type: 'manual', label: 'Manual Start', icon: Play, color: '#848e9c', description: 'Trigger manually', config: {} },
    ],
  },
  {
    name: 'DEX Actions',
    icon: ArrowRightLeft,
    description: 'Interact with decentralized exchanges',
    nodes: [
      { type: 'dex-swap', label: 'Swap Tokens', icon: ArrowRightLeft, color: '#ff007a', description: 'Execute swap on DEX', config: { chain: 'ethereum', dex: 'uniswap-v3', tokenIn: '', tokenOut: '', amount: 0, slippage: 0.5 } },
      { type: 'dex-quote', label: 'Get Quote', icon: Calculator, color: '#00d4aa', description: 'Get swap quote without executing', config: { chain: 'ethereum', dex: 'uniswap-v3', tokenIn: '', tokenOut: '', amount: 0 } },
      { type: 'multi-hop-swap', label: 'Multi-Hop Swap', icon: Repeat, color: '#6366f1', description: 'Swap through multiple pools', config: { chain: 'ethereum', path: [], slippage: 0.5 } },
      { type: 'add-liquidity', label: 'Add Liquidity', icon: Plus, color: '#1e90ff', description: 'Provide liquidity to pool', config: { chain: 'ethereum', dex: 'uniswap-v3', pool: '', amount0: 0, amount1: 0, range: [0, 0] } },
      { type: 'remove-liquidity', label: 'Remove Liquidity', icon: X, color: '#f6465d', description: 'Withdraw from LP position', config: { chain: 'ethereum', tokenId: '', percentage: 100 } },
      { type: 'get-reserves', label: 'Get Reserves', icon: Database, color: '#f0b90b', description: 'Read pool reserves', config: { chain: 'ethereum', dex: 'uniswap-v2', pool: '' } },
      { type: 'check-allowance', label: 'Check Allowance', icon: Shield, color: '#8b5cf6', description: 'Verify token approval', config: { chain: 'ethereum', token: '', spender: '' } },
    ],
  },
  {
    name: 'CEX Actions',
    icon: TrendingUp,
    description: 'Trade on centralized exchanges',
    nodes: [
      { type: 'cex-order', label: 'Place Order', icon: TrendingUp, color: '#00d4aa', description: 'Market/Limit order on CEX', config: { exchange: 'binance', pair: 'BTC-USDT', side: 'buy', type: 'limit', amount: 0, price: 0 } },
      { type: 'cex-cancel', label: 'Cancel Order', icon: X, color: '#f6465d', description: 'Cancel open order', config: { exchange: 'binance', orderId: '' } },
      { type: 'cex-balance', label: 'Get Balance', icon: Wallet, color: '#f0b90b', description: 'Check CEX wallet balance', config: { exchange: 'binance', asset: 'USDT' } },
      { type: 'cex-orderbook', label: 'Get Orderbook', icon: BarChart3, color: '#6366f1', description: 'Fetch orderbook depth', config: { exchange: 'binance', pair: 'BTC-USDT', depth: 20 } },
      { type: 'cex-ticker', label: 'Get Ticker', icon: Activity, color: '#8b5cf6', description: 'Real-time price ticker', config: { exchange: 'binance', pair: 'BTC-USDT' } },
      { type: 'cex-withdraw', label: 'Withdraw', icon: ArrowRightLeft, color: '#f6465d', description: 'Withdraw to wallet', config: { exchange: 'binance', asset: '', amount: 0, address: '', network: 'ethereum' } },
    ],
  },
  {
    name: 'Smart Contract',
    icon: FileCode,
    description: 'Deploy and interact with contracts',
    nodes: [
      { type: 'contract-call', label: 'Call Function', icon: Code, color: '#8b5cf6', description: 'Execute contract function', config: { chain: 'ethereum', contract: '', function: '', args: [], value: 0, abi: [] } },
      { type: 'contract-read', label: 'Read State', icon: Eye, color: '#00d4aa', description: 'Read contract state', config: { chain: 'ethereum', contract: '', function: '', args: [], abi: [] } },
      { type: 'contract-deploy', label: 'Deploy Contract', icon: Cpu, color: '#f0b90b', description: 'Deploy new contract', config: { chain: 'ethereum', bytecode: '', abi: [], args: [] } },
      { type: 'contract-event', label: 'Parse Event', icon: Radio, color: '#6366f1', description: 'Decode event logs', config: { chain: 'ethereum', abi: [], eventName: '' } },
      { type: 'approve-token', label: 'Approve Token', icon: Shield, color: '#f6465d', description: 'Set token allowance', config: { chain: 'ethereum', token: '', spender: '', amount: 'unlimited' } },
      { type: 'multicall', label: 'Multicall', icon: Layers, color: '#1e90ff', description: 'Batch multiple calls', config: { chain: 'ethereum', calls: [] } },
    ],
  },
  {
    name: 'Oracle & Data',
    icon: Link2,
    description: 'External data and price feeds',
    nodes: [
      { type: 'chainlink-price', label: 'Chainlink Price', icon: Link2, color: '#375bd2', description: 'Get Chainlink price feed', config: { chain: 'ethereum', feed: 'ETH/USD' } },
      { type: 'pyth-price', label: 'Pyth Price', icon: Activity, color: '#8b5cf6', description: 'Get Pyth network price', config: { chain: 'ethereum', feed: '' } },
      { type: 'gas-price', label: 'Gas Price', icon: Zap, color: '#f0b90b', description: 'Current gas price', config: { chain: 'ethereum' } },
      { type: 'block-number', label: 'Block Number', icon: Layers, color: '#00d4aa', description: 'Get current block', config: { chain: 'ethereum' } },
      { type: 'token-price', label: 'Token Price', icon: TrendingUp, color: '#6366f1', description: 'Get token price from DEX', config: { chain: 'ethereum', token: '', quote: 'USDC' } },
      { type: 'http-request', label: 'HTTP Request', icon: Globe, color: '#1e90ff', description: 'External API call', config: { url: '', method: 'GET', headers: {}, body: '' } },
    ],
  },
  {
    name: 'DeFi Protocols',
    icon: Layers,
    description: 'Interact with DeFi protocols',
    nodes: [
      { type: 'aave-supply', label: 'Aave Supply', icon: Plus, color: '#B6509E', description: 'Supply assets to Aave', config: { chain: 'ethereum', asset: '', amount: 0 } },
      { type: 'aave-borrow', label: 'Aave Borrow', icon: ArrowRightLeft, color: '#B6509E', description: 'Borrow from Aave', config: { chain: 'ethereum', asset: '', amount: 0, interestMode: 'variable' } },
      { type: 'compound-supply', label: 'Compound Supply', icon: Plus, color: '#00D395', description: 'Supply to Compound', config: { chain: 'ethereum', asset: '', amount: 0 } },
      { type: 'gmx-long', label: 'GMX Long', icon: TrendingUp, color: '#0AC7E8', description: 'Open GMX long position', config: { chain: 'arbitrum', market: 'ETH-USD', size: 0, leverage: 2 } },
      { type: 'gmx-short', label: 'GMX Short', icon: TrendingUp, color: '#0AC7E8', description: 'Open GMX short position', config: { chain: 'arbitrum', market: 'ETH-USD', size: 0, leverage: 2 } },
      { type: 'stake', label: 'Stake Tokens', icon: Shield, color: '#00d4aa', description: 'Stake in protocol', config: { chain: 'ethereum', protocol: '', token: '', amount: 0 } },
    ],
  },
  {
    name: 'Logic',
    icon: GitBranch,
    description: 'Control flow and conditions',
    nodes: [
      { type: 'condition', label: 'IF Condition', icon: GitBranch, color: '#6366f1', description: 'Branch based on condition', config: { condition: '', operator: '>', value: 0 } },
      { type: 'switch', label: 'Switch', icon: Filter, color: '#8b5cf6', description: 'Multiple condition routes', config: { cases: [] } },
      { type: 'loop', label: 'Loop', icon: Repeat, color: '#f0b90b', description: 'Iterate over items', config: { maxIterations: 10 } },
      { type: 'delay', label: 'Delay', icon: Clock, color: '#848e9c', description: 'Wait before continuing', config: { duration: 1000 } },
      { type: 'merge', label: 'Merge', icon: Layers, color: '#00d4aa', description: 'Combine multiple paths', config: {} },
      { type: 'math', label: 'Math Operation', icon: Calculator, color: '#f6465d', description: 'Calculate values', config: { expression: '' } },
      { type: 'set-variable', label: 'Set Variable', icon: Database, color: '#1e90ff', description: 'Store value for later use', config: { name: '', value: '' } },
    ],
  },
  {
    name: 'AI & Analysis',
    icon: Brain,
    description: 'AI-powered decision making',
    nodes: [
      { type: 'sentiment', label: 'Sentiment Analysis', icon: Brain, color: '#8b5cf6', description: 'Analyze market sentiment', config: { source: 'twitter', keywords: [] } },
      { type: 'price-predict', label: 'Price Prediction', icon: TrendingUp, color: '#00d4aa', description: 'ML price forecast', config: { model: 'lstm', horizon: '1h' } },
      { type: 'risk-score', label: 'Risk Assessment', icon: Shield, color: '#f6465d', description: 'Calculate risk metrics', config: { metrics: ['volatility', 'correlation'] } },
      { type: 'anomaly-detect', label: 'Anomaly Detection', icon: Activity, color: '#f0b90b', description: 'Detect unusual patterns', config: { sensitivity: 0.5 } },
      { type: 'llm-agent', label: 'LLM Agent', icon: Brain, color: '#6366f1', description: 'AI decision making', config: { prompt: '', model: 'gpt-4' } },
    ],
  },
  {
    name: 'Notifications',
    icon: Bell,
    description: 'Alerts and notifications',
    nodes: [
      { type: 'telegram', label: 'Telegram Alert', icon: Bell, color: '#0088cc', description: 'Send Telegram message', config: { chatId: '', message: '' } },
      { type: 'discord', label: 'Discord Alert', icon: Bell, color: '#5865f2', description: 'Post to Discord webhook', config: { webhookUrl: '', message: '' } },
      { type: 'email', label: 'Email Alert', icon: Bell, color: '#f6465d', description: 'Send email notification', config: { to: '', subject: '', body: '' } },
      { type: 'push', label: 'Push Notification', icon: Bell, color: '#00d4aa', description: 'Mobile push alert', config: { title: '', body: '' } },
      { type: 'webhook', label: 'Webhook', icon: Globe, color: '#6366f1', description: 'POST to custom URL', config: { url: '', headers: {}, body: '' } },
    ],
  },
];

// Custom node with handles
function CustomNode({ data, selected }: { data: any; selected: boolean }) {
  const NodeIcon = data.icon || Zap;
  return (
    <div
      className={cn(
        'px-4 py-3 rounded-xl min-w-[200px] transition-all duration-200',
        'bg-[#161b22] border-2',
        selected 
          ? 'border-[#00d4aa] shadow-lg shadow-[#00d4aa]/20' 
          : 'border-white/[0.08] hover:border-white/[0.15]'
      )}
    >
      <Handle type="target" position={Position.Top} className="!w-3 !h-3 !bg-[#00d4aa] !border-2 !border-[#0b0e11]" />
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${data.color}20` }}
        >
          <NodeIcon className="h-5 w-5" style={{ color: data.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium text-white text-sm truncate">{data.label}</div>
          <div className="text-xs text-[#848e9c] truncate">{data.description}</div>
        </div>
      </div>
      {data.config && Object.keys(data.config).length > 0 && (
        <div className="mt-2 pt-2 border-t border-white/[0.06]">
          <div className="text-xs text-[#5e6673]">
            {Object.entries(data.config).slice(0, 2).map(([key, val]) => (
              <div key={key} className="flex justify-between">
                <span>{key}:</span>
                <span className="text-[#848e9c] truncate ml-2">{String(val) || '...'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      <Handle type="source" position={Position.Bottom} className="!w-3 !h-3 !bg-[#00d4aa] !border-2 !border-[#0b0e11]" />
    </div>
  );
}

const nodeTypes: NodeTypes = {
  custom: CustomNode,
};

export default function WorkflowEditor() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [showPalette, setShowPalette] = useState(true);
  const [selectedNode, setSelectedNode] = useState<Node | null>(null);
  const [workflowName, setWorkflowName] = useState('Untitled Strategy');
  const [expandedCategory, setExpandedCategory] = useState<string | null>('Triggers');
  const [mode, setMode] = useState<'edit' | 'backtest' | 'paper' | 'live'>('edit');
  const [searchQuery, setSearchQuery] = useState('');

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);

  // Fetch workflow
  const { data: workflow, isLoading } = useQuery({
    queryKey: ['workflow', id],
    queryFn: () => workflowApi.get(id!),
    enabled: !!id && id !== 'new',
  });

  useEffect(() => {
    if (workflow?.data) {
      setWorkflowName(workflow.data.name || 'Untitled Strategy');
      const allNodes = nodeCategories.flatMap((c) => c.nodes);
      const loadedNodes = (workflow.data.nodes || []).map((n: any) => {
        const nodeTypeDef = allNodes.find((nt) => nt.type === n.data?.nodeType);
        return {
          ...n,
          type: 'custom',
          data: {
            ...n.data,
            icon: nodeTypeDef?.icon || Zap,
            color: nodeTypeDef?.color || '#f0b90b',
          },
        };
      });
      setNodes(loadedNodes);
      setEdges(workflow.data.edges || []);
    }
  }, [workflow, setNodes, setEdges]);

  const saveMutation = useMutation({
    mutationFn: () =>
      id === 'new'
        ? workflowApi.create({ name: workflowName, description: '' })
        : workflowApi.update(id!, {
            name: workflowName,
            nodes: nodes.map((n) => ({
              ...n,
              data: { ...n.data, icon: undefined },
            })),
            edges,
          }),
    onSuccess: (response) => {
      toast.success('Strategy saved');
      queryClient.invalidateQueries({ queryKey: ['workflow', id] });
      if (id === 'new' && response.data?.id) {
        navigate(`/workflow/${response.data.id}`, { replace: true });
      }
    },
    onError: () => toast.error('Failed to save strategy'),
  });

  const executeMutation = useMutation({
    mutationFn: () => workflowApi.execute(id!),
    onSuccess: () => toast.success(`Strategy ${mode === 'live' ? 'deployed' : 'started'}`),
    onError: () => toast.error('Failed to execute strategy'),
  });

  const onConnect = useCallback(
    (params: Connection) => setEdges((eds) => addEdge({
      ...params,
      animated: true,
      style: { stroke: '#f0b90b', strokeWidth: 2 },
    }, eds)),
    [setEdges]
  );

  const onNodeClick = useCallback((_: any, node: Node) => {
    setSelectedNode(node);
  }, []);

  const addNode = (nodeType: NodeDef) => {
    const newNode: Node = {
      id: `node_${Date.now()}`,
      type: 'custom',
      position: { x: 350 + Math.random() * 50, y: 100 + nodes.length * 130 },
      data: {
        label: nodeType.label,
        nodeType: nodeType.type,
        icon: nodeType.icon,
        color: nodeType.color,
        description: nodeType.description,
        config: { ...nodeType.config },
      },
    };
    setNodes((nds) => [...nds, newNode]);
    setSelectedNode(newNode);
  };

  const deleteNode = (nodeId: string) => {
    setNodes((nds) => nds.filter((n) => n.id !== nodeId));
    setEdges((eds) => eds.filter((e) => e.source !== nodeId && e.target !== nodeId));
    setSelectedNode(null);
  };

  const updateNodeConfig = (nodeId: string, key: string, value: any) => {
    setNodes((nds) =>
      nds.map((n) =>
        n.id === nodeId
          ? { ...n, data: { ...n.data, config: { ...n.data.config, [key]: value } } }
          : n
      )
    );
    if (selectedNode?.id === nodeId) {
      setSelectedNode((prev) => prev ? {
        ...prev,
        data: { ...prev.data, config: { ...prev.data.config, [key]: value } }
      } : null);
    }
  };

  // Filter nodes by search
  const filteredCategories = nodeCategories.map(cat => ({
    ...cat,
    nodes: cat.nodes.filter((n: NodeDef) => 
      n.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
      n.description.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(cat => cat.nodes.length > 0);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full bg-[#0b0e11]">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="h-full flex bg-[#0b0e11]">
      {/* Node Palette */}
      {showPalette && (
        <div className="w-80 border-r border-white/[0.06] bg-[#12161c] flex flex-col">
          <div className="p-4 border-b border-white/[0.06]">
            <div className="flex items-center gap-2 mb-3">
              <Layers className="h-5 w-5 text-primary" />
              <span className="font-semibold text-white">Smart Contract Nodes</span>
            </div>
            <input
              type="text"
              placeholder="Search nodes..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="input text-sm"
            />
          </div>
          
          <div className="flex-1 overflow-y-auto custom-scrollbar p-3">
            {filteredCategories.map((category) => {
              const CategoryIcon = category.icon;
              const isExpanded = expandedCategory === category.name || searchQuery;
              return (
                <div key={category.name} className="mb-2">
                  <button
                    onClick={() => setExpandedCategory(expandedCategory === category.name ? null : category.name)}
                    className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/[0.04] transition-colors"
                  >
                    <div className="flex items-center gap-2">
                      <CategoryIcon className="h-4 w-4 text-[#848e9c]" />
                      <div className="text-left">
                        <div className="text-sm font-medium text-[#eaecef]">{category.name}</div>
                        <div className="text-xs text-[#5e6673]">{category.description}</div>
                      </div>
                    </div>
                    <ChevronRight className={cn(
                      "h-4 w-4 text-[#848e9c] transition-transform",
                      isExpanded && "rotate-90"
                    )} />
                  </button>
                  
                  {isExpanded && (
                    <div className="mt-1 ml-2 space-y-1">
                      {category.nodes.map((node: NodeDef) => {
                        const Icon = node.icon;
                        return (
                          <button
                            key={node.type}
                            onClick={() => addNode(node)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-white/[0.06] transition-colors group"
                          >
                            <div 
                              className="w-9 h-9 rounded-lg flex items-center justify-center transition-transform group-hover:scale-110"
                              style={{ backgroundColor: `${node.color}15` }}
                            >
                              <Icon className="h-4 w-4" style={{ color: node.color }} />
                            </div>
                            <div className="flex-1 text-left min-w-0">
                              <div className="text-sm text-white truncate">{node.label}</div>
                              <div className="text-xs text-[#5e6673] truncate">{node.description}</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Canvas */}
      <div className="flex-1 relative">
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onNodeClick={onNodeClick}
          onPaneClick={() => setSelectedNode(null)}
          nodeTypes={nodeTypes}
          fitView
          snapToGrid
          snapGrid={[20, 20]}
          defaultEdgeOptions={{
            animated: true,
            style: { stroke: '#00d4aa', strokeWidth: 2 },
          }}
        >
          <Controls className="!bg-[#161b22] !border-white/[0.08] !rounded-xl overflow-hidden" />
          <MiniMap 
            className="!bg-[#12161c] !border-white/[0.08] !rounded-xl overflow-hidden"
            nodeColor="#f0b90b"
            maskColor="rgba(0, 0, 0, 0.7)"
          />
          <Background variant={BackgroundVariant.Dots} gap={20} size={1} color="#2b3139" />

          {/* Top Left */}
          <Panel position="top-left" className="flex items-center gap-3">
            <button
              onClick={() => navigate('/')}
              className="flex items-center gap-2 px-3 py-2 bg-[#161b22] border border-white/[0.08] rounded-xl hover:bg-[#21262d] transition-colors"
            >
              <ArrowLeft className="h-4 w-4 text-[#848e9c]" />
            </button>
            <div className="flex items-center gap-2 px-4 py-2 bg-[#161b22] border border-white/[0.08] rounded-xl">
              <input
                type="text"
                value={workflowName}
                onChange={(e) => setWorkflowName(e.target.value)}
                className="bg-transparent border-none outline-none text-white font-medium text-sm w-56"
              />
            </div>
          </Panel>

          {/* Top Center - Mode Selector */}
          <Panel position="top-center">
            <div className="flex items-center gap-1 p-1 bg-[#161b22] border border-white/[0.08] rounded-xl">
              {[
                { id: 'edit', label: 'Edit', icon: Code },
                { id: 'backtest', label: 'Backtest', icon: TestTube },
                { id: 'paper', label: 'Paper', icon: Eye },
                { id: 'live', label: 'Live', icon: Zap },
              ].map((m) => (
                <button
                  key={m.id}
                  onClick={() => setMode(m.id as any)}
                  className={cn(
                    "flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-colors",
                    mode === m.id
                      ? m.id === 'live' ? "bg-[#0ecb81] text-white" : "bg-[#00d4aa]/10 text-[#00d4aa]"
                      : "text-[#848e9c] hover:text-white"
                  )}
                >
                  <m.icon className="h-4 w-4" />
                  {m.label}
                </button>
              ))}
            </div>
          </Panel>

          {/* Top Right */}
          <Panel position="top-right" className="flex items-center gap-2">
            <button
              onClick={() => setShowPalette(!showPalette)}
              className={cn(
                "flex items-center gap-2 px-3 py-2 rounded-xl border transition-colors",
                showPalette 
                  ? "bg-[#00d4aa]/10 border-[#00d4aa]/30 text-[#00d4aa]" 
                  : "bg-[#161b22] border-white/[0.08] text-white hover:bg-[#21262d]"
              )}
            >
              <Plus className="h-4 w-4" />
            </button>
            <button
              onClick={() => saveMutation.mutate()}
              disabled={saveMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 bg-[#161b22] border border-white/[0.08] rounded-xl hover:bg-[#21262d] transition-colors"
            >
              <Save className="h-4 w-4 text-[#848e9c]" />
            </button>
            <button
              onClick={() => executeMutation.mutate()}
              disabled={executeMutation.isPending || id === 'new' || nodes.length === 0}
              className={cn(
                "flex items-center gap-2 px-4 py-2 rounded-xl transition-colors disabled:opacity-50",
                mode === 'live' ? "bg-[#0ecb81] hover:bg-[#0db876]" : "bg-[#00d4aa] hover:bg-[#00b894]"
              )}
            >
              <Play className="h-4 w-4 text-[#0b0e11]" />
              <span className="text-sm font-medium text-[#0b0e11]">
                {mode === 'backtest' ? 'Run Backtest' : mode === 'paper' ? 'Start Paper' : mode === 'live' ? 'Deploy Live' : 'Test'}
              </span>
            </button>
          </Panel>
        </ReactFlow>
      </div>

      {/* Node Config Panel */}
      {selectedNode && (
        <div className="w-96 border-l border-white/[0.06] bg-[#12161c] flex flex-col">
          <div className="p-4 border-b border-white/[0.06] flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-primary" />
              <span className="font-semibold text-white">Configure Node</span>
            </div>
            <button
              onClick={() => setSelectedNode(null)}
              className="p-1.5 rounded-lg text-[#848e9c] hover:text-white hover:bg-white/[0.06] transition-colors"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
          
          <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-4">
            {/* Node Info */}
            <div className="flex items-center gap-3 p-3 bg-[#181a20] rounded-xl">
              {selectedNode.data.icon && (
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${selectedNode.data.color}20` }}
                >
                  <selectedNode.data.icon className="h-5 w-5" style={{ color: selectedNode.data.color }} />
                </div>
              )}
              <div>
                <div className="font-medium text-white">{selectedNode.data.label}</div>
                <div className="text-xs text-[#848e9c]">{selectedNode.data.nodeType}</div>
              </div>
            </div>

            {/* Label */}
            <div>
              <label className="block text-xs font-medium text-[#848e9c] uppercase tracking-wider mb-2">
                Label
              </label>
              <input
                type="text"
                value={selectedNode.data.label}
                onChange={(e) => {
                  setNodes((nds) =>
                    nds.map((n) =>
                      n.id === selectedNode.id
                        ? { ...n, data: { ...n.data, label: e.target.value } }
                        : n
                    )
                  );
                }}
                className="input"
              />
            </div>

            {/* Dynamic Config Fields */}
            {selectedNode.data.config && Object.entries(selectedNode.data.config).map(([key, value]) => (
              <div key={key}>
                <label className="block text-xs font-medium text-[#848e9c] uppercase tracking-wider mb-2">
                  {key.replace(/([A-Z])/g, ' $1').trim()}
                </label>
                {typeof value === 'boolean' ? (
                  <button
                    onClick={() => updateNodeConfig(selectedNode.id, key, !value)}
                    className={cn(
                      "w-12 h-7 rounded-full transition-colors relative",
                      value ? "bg-[#0ecb81]" : "bg-[#2b3139]"
                    )}
                  >
                    <div className={cn(
                      "absolute top-1 w-5 h-5 rounded-full bg-white transition-transform",
                      value ? "translate-x-6" : "translate-x-1"
                    )} />
                  </button>
                ) : key === 'condition' || key === 'side' || key === 'type' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {key === 'condition' && ['above', 'below', 'crosses'].map(o => <option key={o} value={o}>{o}</option>)}
                    {key === 'side' && ['buy', 'sell'].map(o => <option key={o} value={o}>{o}</option>)}
                    {key === 'type' && ['market', 'limit', 'stop-limit'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : key === 'chain' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => {
                      updateNodeConfig(selectedNode.id, key, e.target.value);
                      // Reset DEX when chain changes
                      const dexOptions = DEX_PROTOCOLS[e.target.value] || [];
                      if (selectedNode.data.config?.dex && !dexOptions.includes(selectedNode.data.config.dex)) {
                        updateNodeConfig(selectedNode.id, 'dex', dexOptions[0] || '');
                      }
                    }}
                    className="select"
                  >
                    {SUPPORTED_CHAINS.map(chain => (
                      <option key={chain.id} value={chain.id}>
                        {chain.name} ({chain.symbol})
                      </option>
                    ))}
                  </select>
                ) : key === 'dex' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {(DEX_PROTOCOLS[selectedNode.data.config?.chain || 'ethereum'] || ['uniswap-v3']).map(dex => (
                      <option key={dex} value={dex}>{dex}</option>
                    ))}
                  </select>
                ) : key === 'exchange' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {['binance', 'coinbase', 'kraken', 'bybit', 'okx', 'kucoin', 'mexc'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : key === 'oracle' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {['chainlink', 'pyth', 'band', 'dia', 'tellor'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : key === 'network' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {SUPPORTED_CHAINS.map(chain => (
                      <option key={chain.id} value={chain.id}>{chain.name}</option>
                    ))}
                  </select>
                ) : key === 'interestMode' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {['variable', 'stable'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : key === 'model' && selectedNode.data.nodeType === 'llm-agent' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {['gpt-4', 'gpt-3.5-turbo', 'claude-3', 'llama-3', 'custom'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : key === 'method' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : key === 'filter' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {['swap', 'transfer', 'liquidity', 'all'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : key === 'source' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {['twitter', 'reddit', 'discord', 'news', 'all'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : key === 'interval' ? (
                  <select
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="select"
                  >
                    {['1m', '5m', '15m', '30m', '1h', '4h', '1d', '1w'].map(o => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : typeof value === 'number' ? (
                  <input
                    type="number"
                    value={value}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, parseFloat(e.target.value) || 0)}
                    className="input"
                  />
                ) : Array.isArray(value) ? (
                  <textarea
                    value={JSON.stringify(value, null, 2)}
                    onChange={(e) => {
                      try {
                        updateNodeConfig(selectedNode.id, key, JSON.parse(e.target.value));
                      } catch {}
                    }}
                    className="input min-h-[80px] font-mono text-xs"
                    placeholder="[]"
                  />
                ) : (
                  <input
                    type="text"
                    value={String(value)}
                    onChange={(e) => updateNodeConfig(selectedNode.id, key, e.target.value)}
                    className="input"
                    placeholder={`Enter ${key}...`}
                  />
                )}
              </div>
            ))}

            {/* Actions */}
            <div className="pt-4 border-t border-white/[0.06]">
              <div className="flex gap-2">
                <button
                  onClick={() => {
                    const newNode = { ...selectedNode, id: `node_${Date.now()}`, position: { x: selectedNode.position.x + 50, y: selectedNode.position.y + 50 } };
                    setNodes((nds) => [...nds, newNode]);
                  }}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-white/[0.04] rounded-lg hover:bg-white/[0.08] transition-colors"
                >
                  <Copy className="h-4 w-4 text-[#848e9c]" />
                  <span className="text-sm text-white">Duplicate</span>
                </button>
                <button
                  onClick={() => deleteNode(selectedNode.id)}
                  className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-[#f6465d]/10 rounded-lg hover:bg-[#f6465d]/20 transition-colors"
                >
                  <Trash2 className="h-4 w-4 text-[#f6465d]" />
                  <span className="text-sm text-[#f6465d]">Delete</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
